import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useThemeUI } from "theme-ui";
import { ForgotPasswordLink } from "./styled";

import TextInput from "components/forms/TextInput";
import BasicButton from "components/forms/BasicButton";
import PasswordInput from "components/forms/PasswordInput";
import Checkbox from "components/forms/Checkbox";
import LoginCard from "components/ui/LoginCard";
import H2 from "components/typography/H2";
import { useToast } from "components/toast";

import { doLogin } from "api/Auth";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import { identifyUser } from "helpers/zohoMA";
import { resetLoginModal } from "helpers/loginModal";

const Login = () => {

  const { setUser } = useContext<any>(UserContext);
  const navigate = useNavigate();
  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get('redirect');

  const toast = useToast();

  const [responseErrors, setResponseErrors] = useState(false);

  const _onSubmit = async (values: any, actions: any) => {
    localStorage.removeItem("user");
    void trackActivity({
      module: "auth",
      action: "login_attempt",
      actionType: "click",
      metadata: {
        email: values.email,
        remember: values.rememberMe ? values.rememberMe : false,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    const res = await doLogin({ email: values.email, password: values.password, rememberMe : values.rememberMe ? values.rememberMe : false });

    if (res?.success) {   
      setUser({...res?.user })
      localStorage.setItem("user", JSON.stringify({user_id : res.user.id, tokens : {...res.tokens}}));
      resetLoginModal(); // one pop-up per login: a fresh login gets a fresh slot
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.tokens.accessToken}`;
      
      // Identify user in Zoho MA for popup targeting (clients only)
      console.log('🔍 Login: Checking if user should be identified in Zoho MA');
      console.log('🔍 Login: accessLevel:', res.user?.accessLevel);
      console.log('🔍 Login: has client object?', !!res.user?.client);
      console.log('🔍 Login: full user object:', res.user);
      
      if (res.user?.accessLevel === "isClient" && res.user?.client) {
        const email = res.user.client.contactEmail || res.user.client.billingEmail || res.user.email;
        const name = res.user.client.name || `${res.user.firstName} ${res.user.lastName}`;
        console.log('✅ Login: User is a client, identifying in Zoho MA');
        console.log('✅ Login: Using email:', email);
        console.log('✅ Login: Using name:', name);
        identifyUser(email, { name });
      } else {
        console.log('⏭️ Login: User is not a client, skipping Zoho MA identification');
      }
      
      // All users go to dashboard (or redirect URL if specified)
      navigate(redirectUrl || "/");
    } else {
      localStorage.removeItem("user");
      toast.add(res?.message ? res.message : "Error loging in, please try again later" , theme?.colors?.danger);
      setResponseErrors(true);
      setUser(null);      
    }
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email.").required("This field is required."),
    password: Yup.string().required("This field is required."),
  });

  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    document.title = process.env.REACT_APP_NAME + " | Login";
    void trackActivity({
      module: "auth",
      action: "login_view",
      actionType: "view",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, []);

  return (
    <LoginCard>        
      <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
        Login
      </H2>
      <Formik initialValues={{ email: "", password: "", rememberMe: false }} onSubmit={_onSubmit} validationSchema={LoginSchema}>
        {({ isSubmitting, errors, submitCount, values, setFieldValue }) => {
          return (
            <Form noValidate autoComplete="off">
              <TextInput 
                name="email" 
                label="Email" 
                value={values.email || ""}
                onChange={(val: any) => setFieldValue("email",val)}
                $errors={errors.email && submitCount > 0 ? errors.email : null} 
                $responseErrors={responseErrors}
              />
              <PasswordInput 
                name="password" 
                label="Password" 
                value={values.password || ""}
                onChange={(val: any) => setFieldValue("password",val)}
                $errors={errors.password && submitCount > 0 ? errors.password : null} 
                $responseErrors={responseErrors}
              />
              <Checkbox 
                label="Remember Me" 
                name="rememberMe" 
                checked={values.rememberMe}
                onChange={(val: any) => { setFieldValue("rememberMe",val);}}
              />
              <BasicButton
                type="submit"
                $submitting={isSubmitting}                      
                styles={{width:"100%",margin : "40px auto 0px auto"}}
                $loadingColor="#fff"
                keepText={false}
              >Login</BasicButton>
            </Form>
          );
        }}
      </Formik>
      <ForgotPasswordLink>
        <Link to={"/forgot-password"}>Forgot password?</Link>
      </ForgotPasswordLink>
    </LoginCard>
  );
};

export default Login;
